import { useState, useEffect, useCallback, useRef } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { aiDashboardChat, getAIDashboardChatHistory } from "store/aiDashboard"
import { getInitialChatMessages } from "../constants"
import { mapHistoryToMessages } from "../utils"

const CHAT_SESSION_KEY = "dash_chat_session_id"

export default function useDashChat(scrollToBottom) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [input, setInput] = useState("")
  const [messages, setMessages] = useState(() => getInitialChatMessages(t))
  const [loading, setLoading] = useState(false)
  const sessionIdRef = useRef(localStorage.getItem(CHAT_SESSION_KEY) || null)

  useEffect(() => {
    const savedSessionId = localStorage.getItem(CHAT_SESSION_KEY)
    if (!savedSessionId) return
    dispatch(getAIDashboardChatHistory(savedSessionId))
      .then((res) => {
        const history = res?.messages ?? []
        if (Array.isArray(history) && history.length > 0) {
          setMessages(mapHistoryToMessages(history))
          setTimeout(scrollToBottom, 100)
        }
      })
  }, [dispatch, scrollToBottom])

  const buildAiMessage = useCallback((data) => {
    const text = data?.message || t("ai_dashboard.chat_fallback_response")
    const isChart = data?.response_type === "chart"
    const isClarification = data?.response_type === "clarification"
    return {
      id: Date.now() + 1,
      sender: "ai",
      text,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      chartPayload: isChart
        ? { chart_title: data.chart_title, chart_type: data.chart_type, chart_data: data.chart_data, source_prompt: data.source_prompt }
        : null,
      options: isClarification ? data.options : null
    }
  }, [t])

  const persistSession = (data) => {
    if (data?.session_id) {
      sessionIdRef.current = data.session_id
      localStorage.setItem(CHAT_SESSION_KEY, data.session_id)
    }
  }

  const handleSend = () => {
    const message = input.trim()
    if (!message || loading) return

    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: message,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
    setMessages((prev) => [...prev, userMsg])
    setInput("")
    setLoading(true)

    dispatch(aiDashboardChat(message, sessionIdRef.current))
      .then((data) => { persistSession(data); setMessages((prev) => [...prev, buildAiMessage(data)]) })
      .catch(() => setMessages((prev) => [...prev, { id: Date.now() + 1, sender: "ai", text: t("ai_dashboard.chat_error"), time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]))
      .finally(() => { setLoading(false); setTimeout(scrollToBottom, 100) })
  }

  const handleChartSaved = useCallback((msgId) => {
    setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, chartPayload: null, widgetSaved: true } : m))
  }, [])

  const handleChartDismiss = useCallback((msgId) => {
    setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, chartPayload: null } : m))
  }, [])

  const handleOptionSelect = useCallback((msgId, opt) => {
    setMessages((prev) => prev.map((m) => m.id === msgId ? { ...m, options: null } : m))
    const userMsg = {
      id: Date.now(),
      sender: "user",
      text: opt.label,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    }
    setMessages((prev) => [...prev, userMsg])
    setLoading(true)

    dispatch(aiDashboardChat(opt.value, sessionIdRef.current))
      .then((data) => { persistSession(data); setMessages((prev) => [...prev, buildAiMessage(data)]) })
      .catch(() => setMessages((prev) => [...prev, { id: Date.now() + 1, sender: "ai", text: t("ai_dashboard.chat_error"), time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }]))
      .finally(() => { setLoading(false); setTimeout(scrollToBottom, 100) })
  }, [dispatch, scrollToBottom, t, buildAiMessage])

  return {
    input, setInput,
    messages,
    loading,
    handleSend,
    handleChartSaved,
    handleChartDismiss,
    handleOptionSelect,
  }
}
